/** Grid/list view of storage files and subfolders in the current folder */

import { Folder, FileIcon, Download, Trash2, Copy, HardDrive, Play } from 'lucide-react'
import { cn } from '../../lib/utils'
import { API_BASE } from '../../lib/api-client'
import { ExtractionBadge } from './extraction-badge'
import { SummaryBlock } from './summary-block'
import { DescriptionEditor } from './description-editor'
import { RegenerateSummaryButton } from './regenerate-summary-button'
import { isPreviewable } from './storage-preview-modal'
import { useUpdateUploadDescription, useRegenerateSummary } from '../../hooks/use-uploads'
import type { StorageFolderNode, StorageFile } from '../../hooks/use-storage'

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

// ── Folder card ───────────────────────────────────────────────────────────────

interface FolderCardProps {
  folder: StorageFolderNode
  isDark: boolean
  onOpen: (id: string) => void
}

function FolderCard({ folder, isDark, onOpen }: FolderCardProps) {
  return (
    <button
      onClick={() => onOpen(folder.id)}
      className={cn(
        'group flex items-center gap-2 rounded-lg border px-3 py-2.5 text-left transition-colors',
        isDark
          ? 'border-white/[0.06] bg-surface-2 hover:bg-surface-3 text-neutral-200'
          : 'border-neutral-200 bg-white hover:bg-neutral-50 text-neutral-800',
      )}
    >
      <Folder className="h-5 w-5 shrink-0 text-brand-400" />
      <div className="flex-1 min-w-0">
        <p className="truncate text-xs font-medium">{folder.name}</p>
        <p className={cn('text-[10px]', isDark ? 'text-neutral-500' : 'text-neutral-400')}>
          {[
            folder.children.length > 0 && `${folder.children.length} subfolder${folder.children.length !== 1 ? 's' : ''}`,
            folder.fileCount > 0 && `${folder.fileCount} file${folder.fileCount !== 1 ? 's' : ''}`,
          ].filter(Boolean).join(' · ') || 'Empty'}
        </p>
      </div>
    </button>
  )
}

// ── File card (grid mode) ─────────────────────────────────────────────────────

interface FileCardProps {
  file: StorageFile
  isDark: boolean
  onDelete: (id: string, filename: string) => void
  onPreview?: (file: StorageFile) => void
}

function FileCard({ file, isDark, onDelete, onPreview }: FileCardProps) {
  const isImage = file.contentType.startsWith('image/')
  const isVideo = file.contentType.startsWith('video/')
  const canPreview = isPreviewable(file.contentType)

  const updateDesc = useUpdateUploadDescription()
  const regenSummary = useRegenerateSummary()

  return (
    <div
      className={cn(
        'group relative overflow-hidden rounded-lg border',
        isDark ? 'border-white/[0.06] bg-surface-2' : 'border-neutral-200 bg-white',
      )}
    >
      {/* Preview — only the media region is the click target so summary/description
          inputs below stay interactive without bubbling into preview-open. */}
      <div
        className={cn('h-24', canPreview && 'cursor-pointer')}
        onClick={canPreview && onPreview ? () => onPreview(file) : undefined}
      >
        {isImage ? (
          <img
            src={`${API_BASE}/api/files/${file.fileKey}`}
            alt={file.filename}
            className="h-full w-full object-cover"
            loading="lazy"
          />
        ) : isVideo ? (
          <div className={cn('flex h-full items-center justify-center', isDark ? 'bg-surface-3' : 'bg-neutral-50')}>
            <Play className="h-7 w-7 text-brand-400" />
          </div>
        ) : (
          <div className={cn('flex h-full items-center justify-center', isDark ? 'bg-surface-3' : 'bg-neutral-50')}>
            <FileIcon className="h-7 w-7 text-neutral-500" />
          </div>
        )}
      </div>

      {/* Filename + regenerate */}
      <div className="flex items-center justify-between gap-1 px-2 pt-2">
        <p className={cn('truncate text-xs font-medium', isDark ? 'text-neutral-200' : 'text-neutral-800')}>
          {file.filename}
        </p>
        <RegenerateSummaryButton
          status={file.extractionStatus}
          lastSummaryAt={file.lastSummaryAt}
          pending={regenSummary.isPending}
          onClick={() => regenSummary.mutate(file.id)}
        />
      </div>

      {/* Size + extraction badge */}
      <div className="flex items-center justify-between px-2 pb-1">
        <span className={cn('text-[10px]', isDark ? 'text-neutral-500' : 'text-neutral-400')}>
          {formatBytes(file.sizeBytes)}
        </span>
        <ExtractionBadge status={file.extractionStatus} uploadId={file.id} />
      </div>

      {/* AI summary */}
      <SummaryBlock
        summary={file.summary}
        status={file.extractionStatus}
        extractionMethod={file.extractionMethod}
        contentType={file.contentType}
        isDark={isDark}
        onGenerate={() => regenSummary.mutate(file.id)}
        generatePending={regenSummary.isPending}
      />

      {/* User description */}
      <DescriptionEditor
        value={file.description}
        isDark={isDark}
        onSave={(description) => updateDesc.mutate({ id: file.id, description })}
        saving={updateDesc.isPending}
      />

      {/* Hover actions */}
      <div className="absolute top-1 right-1 flex gap-1 opacity-0 transition-opacity group-hover:opacity-100" onClick={(e) => e.stopPropagation()}>
        <button
          onClick={() => void navigator.clipboard.writeText(`${API_BASE}/api/files/${file.fileKey}`)}
          className="rounded-md bg-black/60 p-1 text-white hover:bg-black/80"
          title="Copy URL"
        >
          <Copy className="h-3 w-3" />
        </button>
        <a
          href={`${API_BASE}/api/files/${file.fileKey}`}
          download={file.filename}
          className="rounded-md bg-black/60 p-1 text-white hover:bg-black/80"
          title="Download"
        >
          <Download className="h-3 w-3" />
        </a>
        <button
          onClick={() => onDelete(file.id, file.filename)}
          className="rounded-md bg-black/60 p-1 text-red-400 hover:bg-black/80"
          title="Delete"
        >
          <Trash2 className="h-3 w-3" />
        </button>
      </div>
    </div>
  )
}

// ── File row (list mode) ──────────────────────────────────────────────────────

function FileRow({ file, isDark, onDelete, onPreview }: FileCardProps) {
  const canPreview = isPreviewable(file.contentType)
  const isVideo = file.contentType.startsWith('video/')
  return (
    <div
      className={cn(
        'group flex items-center gap-3 rounded-lg border px-3 py-2',
        isDark ? 'border-white/[0.06] bg-surface-2' : 'border-neutral-200 bg-white',
        canPreview && 'cursor-pointer',
      )}
      onClick={canPreview && onPreview ? () => onPreview(file) : undefined}
    >
      {isVideo ? (
        <Play className={cn('h-4 w-4 shrink-0 text-brand-400')} />
      ) : (
        <FileIcon className={cn('h-4 w-4 shrink-0', isDark ? 'text-neutral-500' : 'text-neutral-400')} />
      )}
      <span className={cn('flex-1 truncate text-xs', isDark ? 'text-neutral-200' : 'text-neutral-800')}>
        {file.filename}
      </span>
      <span className={cn('shrink-0 text-[10px]', isDark ? 'text-neutral-500' : 'text-neutral-400')}>
        {formatBytes(file.sizeBytes)}
      </span>
      <ExtractionBadge status={file.extractionStatus} uploadId={file.id} />
      <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100">
        <a
          href={`${API_BASE}/api/files/${file.fileKey}`}
          download={file.filename}
          className={cn('rounded p-1', isDark ? 'text-neutral-400 hover:text-neutral-200' : 'text-neutral-400 hover:text-neutral-700')}
          title="Download"
        >
          <Download className="h-3.5 w-3.5" />
        </a>
        <button
          onClick={() => onDelete(file.id, file.filename)}
          className="rounded p-1 text-red-400 hover:text-red-300"
          title="Delete"
        >
          <Trash2 className="h-3.5 w-3.5" />
        </button>
      </div>
    </div>
  )
}

// ── Main grid component ───────────────────────────────────────────────────────

interface StorageFileGridProps {
  /** Subfolders in current folder (from tree, filtered to current parent) */
  subfolders: StorageFolderNode[]
  files: StorageFile[]
  viewMode: 'grid' | 'list'
  isDark: boolean
  onFolderOpen: (id: string) => void
  onFileDelete: (id: string, filename: string) => void
  onFilePreview?: (file: StorageFile) => void
  isLoading: boolean
}

export function StorageFileGrid({
  subfolders,
  files,
  viewMode,
  isDark,
  onFolderOpen,
  onFileDelete,
  onFilePreview,
  isLoading,
}: StorageFileGridProps) {
  const isEmpty = subfolders.length === 0 && files.length === 0

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-16">
        <div className="h-5 w-5 animate-spin rounded-full border-2 border-neutral-400 border-t-brand-500" />
      </div>
    )
  }

  if (isEmpty) {
    return (
      <div className={cn('flex flex-col items-center gap-2 rounded-lg border border-dashed py-16', isDark ? 'border-white/[0.08]' : 'border-neutral-300')}>
        <HardDrive className={cn('h-8 w-8', isDark ? 'text-neutral-600' : 'text-neutral-400')} />
        <p className={cn('text-sm', isDark ? 'text-neutral-500' : 'text-neutral-400')}>
          This folder is empty
        </p>
      </div>
    )
  }

  if (viewMode === 'list') {
    return (
      <div className="space-y-1">
        {subfolders.map((folder) => (
          <button
            key={folder.id}
            onClick={() => onFolderOpen(folder.id)}
            className={cn(
              'flex w-full items-center gap-3 rounded-lg border px-3 py-2 text-left',
              isDark ? 'border-white/[0.06] bg-surface-2 text-neutral-200 hover:bg-surface-3' : 'border-neutral-200 bg-white text-neutral-800 hover:bg-neutral-50',
            )}
          >
            <Folder className="h-4 w-4 shrink-0 text-brand-400" />
            <span className="flex-1 truncate text-xs font-medium">{folder.name}</span>
            <span className={cn('shrink-0 text-[10px]', isDark ? 'text-neutral-500' : 'text-neutral-400')}>
              {[
                folder.children.length > 0 && `${folder.children.length} subfolder${folder.children.length !== 1 ? 's' : ''}`,
                folder.fileCount > 0 && `${folder.fileCount} file${folder.fileCount !== 1 ? 's' : ''}`,
              ].filter(Boolean).join(' · ') || 'Empty'}
            </span>
          </button>
        ))}
        {files.map((file) => (
          <FileRow key={file.id} file={file} isDark={isDark} onDelete={onFileDelete} onPreview={onFilePreview} />
        ))}
      </div>
    )
  }

  return (
    <div>
      {/* Subfolders row */}
      {subfolders.length > 0 && (
        <div className="mb-4">
          <p className={cn('mb-2 text-[10px] font-semibold uppercase tracking-wider', isDark ? 'text-neutral-500' : 'text-neutral-400')}>
            Folders
          </p>
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-4">
            {subfolders.map((folder) => (
              <FolderCard key={folder.id} folder={folder} isDark={isDark} onOpen={onFolderOpen} />
            ))}
          </div>
        </div>
      )}

      {/* Files grid */}
      {files.length > 0 && (
        <div>
          {subfolders.length > 0 && (
            <p className={cn('mb-2 text-[10px] font-semibold uppercase tracking-wider', isDark ? 'text-neutral-500' : 'text-neutral-400')}>
              Files
            </p>
          )}
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-4">
            {files.map((file) => (
              <FileCard key={file.id} file={file} isDark={isDark} onDelete={onFileDelete} onPreview={onFilePreview} />
            ))}
          </div>
        </div>
      )}
    </div>
  )
}
